/**
 * RTK Query API slice with custom baseQuery and automatic token refresh.
 * HTTP for CRUD operations, WS-first pattern for real-time subscriptions.
 */

import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { getToken, refreshToken, clearAuth } from '@/lib/auth';
import type {
  Meeting,
  MeetingSubscriber,
  Repository,
  Branch,
  DaytonaInstance,
  SearchQuery,
  SearchResult,
  IndexStatus,
  Task,
  CreateTaskDto,
  UpdateTaskDto,
  TaskStatus,
  StorageSource,
  StorageAccount,
  StorageCollection,
  StorageCollectionSettings,
  StorageFile,
  StorageFileVersion,
  StorageUpload,
  StorageProviderDescriptor,
  StorageStats,
} from '@/types';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Track if a refresh is in progress to avoid concurrent refresh requests
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

/**
 * Custom baseQuery that wraps fetchBaseQuery with automatic token refresh on 401.
 */
const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers) => {
      const token = getToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
    timeout: 30_000,
  });

  let result = await baseQuery(args, api, extraOptions);

  // If we get a 401, try to refresh the token
  if (result.error && (result.error as FetchBaseQueryError).status === 401) {
    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve) => {
        addRefreshSubscriber((token: string) => {
          // Retry the original request with the new token
          const retryArgs =
            typeof args === 'string'
              ? { url: args, headers: { Authorization: `Bearer ${token}` } }
              : {
                  ...args,
                  headers: { ...(args as any).headers, Authorization: `Bearer ${token}` },
                };

          Promise.resolve(baseQuery(retryArgs, api, extraOptions)).then(resolve);
        });
      });
    }

    isRefreshing = true;

    try {
      // Try to refresh the token
      await refreshToken();
      const newToken = getToken();

      if (newToken) {
        // Notify all queued requests
        onRefreshed(newToken);

        // Retry the original request with the new token
        const retryArgs =
          typeof args === 'string'
            ? { url: args, headers: { Authorization: `Bearer ${newToken}` } }
            : {
                ...args,
                headers: { ...(args as any).headers, Authorization: `Bearer ${newToken}` },
              };

        result = await Promise.resolve(baseQuery(retryArgs, api, extraOptions));
      } else {
        // Refresh succeeded but no token available - force logout
        clearAuth();
        window.location.href = '/login';
        return result;
      }
    } catch (error) {
      // Refresh failed - force logout
      clearAuth();
      window.location.href = '/login';
      return result;
    } finally {
      isRefreshing = false;
    }
  }

  return result;
};

/**
 * All RTK Query cache tags. Declared as a `const` tuple so the literal union
 * `TagType` can be derived as `(typeof TAG_TYPES)[number]`.
 *
 * NOTE: RTK Query's returned `api` object does not expose `tagTypes` at
 * runtime or in its type, so this tuple is the single source of truth for both
 * the literal union and the value passed to `createApi`.
 */
export const TAG_TYPES = [
  'User',
  'Policy',
  'Service',
  'Job',
  'Dashboard',
  'Settings',
  'ApiKey',
  'FeatureFlag',
  'Meeting',
  'Repository',
  'Branch',
  'Instance',
  'IndexJob',
  'Planning',
  'StorageSource',
  'StorageAccount',
  'StorageCollection',
  'StorageFile',
  'StorageUpload',
  'StorageProvider',
  'Webhook',
  'WebhookLog',
  'AgentProvider',
  'AgentModel',
  'AgentModelCollection',
  'AgentFactory',
  'AgentTrigger',
  'AgentConversation',
  'AgentMessage',
  'AgentRun',
  'AgentQueue',
  'Organization',
  'OrganizationFull',
  'OrgMember',
  'UserGroup',
  'UserGroupMember',
  'Role',
  'RoleAssignment',
  'Project',
  'ProjectResource',
  'Approval',
  'Vault',
  'Voice',
  'Sandbox',
  'BulkOperation',
  'IndexerSource',
  'IndexerGraph',
  'IndexerSearch',
  'Order',
  'Tender',
  'StoreEdge',
  'Ticketing',
  'DirectoryStore',
  'DirectoryWorkspace',
  'Customer',
  'CustomerShare',
  'SimpleRole',
  'ResourceShare',
  'CatalogProduct',
  'Discount',
  'Kiosk',
  'Subscription',
  // Mailbox registry (email service, P6-T2 admin-ui surface).
  'Mailbox',
  // Phase 8a media catalog (P4-T06 realtime maps; endpoints tag with this in
  // later P4 UI tasks).
  'Media',
] as const;

export type TagType = (typeof TAG_TYPES)[number];

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: TAG_TYPES,
  endpoints: (builder) => ({
    // ── Dashboard ──────────────────────────────────────────────────────────
    getDashboardStats: builder.query<unknown, void>({
      query: () => '/dashboard/stats',
      providesTags: ['Dashboard'],
    }),
    getRecentActivity: builder.query({
      query: (params?: { limit?: number }) => ({
        url: '/dashboard/activity',
        params,
      }),
      providesTags: ['Dashboard'],
    }),

    // ── Users ──────────────────────────────────────────────────────────────
    getUsers: builder.query({
      query: (params?: { page?: number; pageSize?: number; search?: string }) => ({
        url: '/users',
        params,
      }),
      providesTags: ['User'],
    }),
    getUser: builder.query({
      query: (id: string) => `/users/${id}`,
      providesTags: ['User'],
    }),
    createUser: builder.mutation({
      query: (body) => ({ url: '/users', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/users/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['User'],
    }),
    deleteUser: builder.mutation({
      query: (id: string) => ({ url: `/users/${id}`, method: 'DELETE' }),
      invalidatesTags: ['User'],
    }),
    deactivateUser: builder.mutation<void, string>({
      query: (id) => ({ url: `/users/${id}/deactivate`, method: 'POST' }),
      invalidatesTags: ['User'],
    }),

    // ── Policies ───────────────────────────────────────────────────────────
    getPolicies: builder.query({
      query: (params?: {
        page?: number;
        pageSize?: number;
        search?: string;
        ownerId?: string;
      }) => ({
        url: '/policies',
        params,
      }),
      providesTags: ['Policy'],
    }),
    getPolicy: builder.query({
      query: (id: string) => `/policies/${id}`,
      providesTags: ['Policy'],
    }),
    createPolicy: builder.mutation({
      query: (body) => ({ url: '/policies', method: 'POST', body }),
      invalidatesTags: ['Policy'],
    }),
    updatePolicy: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/policies/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Policy'],
    }),
    deletePolicy: builder.mutation({
      query: (id: string) => ({ url: `/policies/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Policy'],
    }),
    /** POST /policies/:id/reactivate — undo a cascade-disable on one policy. */
    reactivatePolicy: builder.mutation({
      query: (id: string) => ({ url: `/policies/${id}/reactivate`, method: 'POST' }),
      invalidatesTags: ['Policy'],
    }),
    evaluatePolicy: builder.mutation({
      query: (body) => ({ url: '/policies/evaluate', method: 'POST', body }),
    }),

    // ── Jobs ───────────────────────────────────────────────────────────────
    getJobs: builder.query({
      query: (params?: { status?: string; page?: number; pageSize?: number }) => ({
        url: '/jobs',
        params,
      }),
      providesTags: ['Job'],
    }),
    getJobCounts: builder.query<unknown, void>({
      query: () => '/jobs/counts',
      providesTags: ['Job'],
    }),
    getJob: builder.query({
      query: (id: string) => `/jobs/${id}`,
      providesTags: ['Job'],
    }),
    cancelJob: builder.mutation({
      query: (id: string) => ({ url: `/jobs/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['Job'],
    }),

    // ── Services ───────────────────────────────────────────────────────────
    getServices: builder.query<unknown, void>({
      query: () => '/services',
      providesTags: ['Service'],
    }),
    getService: builder.query({
      query: (id: string) => `/services/${id}`,
      providesTags: ['Service'],
    }),

    // ── Settings ───────────────────────────────────────────────────────────
    getEnvironmentConfig: builder.query<unknown, void>({
      query: () => '/settings/env',
      providesTags: ['Settings'],
    }),
    getApiKeys: builder.query<unknown, void>({
      query: () => '/auth/api-keys',
      providesTags: ['ApiKey'],
    }),
    createApiKey: builder.mutation({
      query: (body) => ({ url: '/auth/api-keys', method: 'POST', body }),
      invalidatesTags: ['ApiKey'],
    }),
    revokeApiKey: builder.mutation({
      query: (id: string) => ({ url: `/auth/api-keys/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ApiKey'],
    }),
    getFeatureFlags: builder.query<unknown, void>({
      query: () => '/settings/feature-flags',
      providesTags: ['FeatureFlag'],
    }),
    toggleFeatureFlag: builder.mutation({
      query: ({ key, enabled }) => ({
        url: `/settings/feature-flags/${key}`,
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: ['FeatureFlag'],
    }),

    // ── Meetings ──────────────────────────────────────────────────────────
    getMeetings: builder.query<Meeting[], { status?: string } | void>({
      query: (params) => ({
        url: `/meetings${params?.status ? `?status=${params.status}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: Meeting[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Meeting' as const, id: _id })),
              { type: 'Meeting', id: 'LIST' },
            ]
          : [{ type: 'Meeting', id: 'LIST' }],
    }),

    getMeeting: builder.query<Meeting, string>({
      query: (meetingId) => `/meetings/${meetingId}`,
      providesTags: (result) => (result ? [{ type: 'Meeting', id: result._id }] : []),
    }),

    getMeetingSubscribers: builder.query<MeetingSubscriber[], string>({
      query: (meetingId) => `/meetings/${meetingId}/subscribers`,
      providesTags: (_result, _error, meetingId) => [{ type: 'Meeting', id: `subs-${meetingId}` }],
    }),

    createMeeting: builder.mutation<Meeting, Partial<Meeting>>({
      query: (body) => ({
        url: '/meetings',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    updateMeeting: builder.mutation<Meeting, { meetingId: string; body: Partial<Meeting> }>({
      query: ({ meetingId, body }) => ({
        url: `/meetings/${meetingId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Meeting', id: result._id },
              { type: 'Meeting', id: 'LIST' },
            ]
          : [{ type: 'Meeting', id: 'LIST' }],
    }),

    startMeeting: builder.mutation<Meeting, string>({
      query: (meetingId) => ({
        url: `/meetings/${meetingId}/start`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    endMeeting: builder.mutation<Meeting, string>({
      query: (meetingId) => ({
        url: `/meetings/${meetingId}/end`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    deleteMeeting: builder.mutation<{ success: boolean }, string>({
      query: (meetingId) => ({
        url: `/meetings/${meetingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    subscribeToMeeting: builder.mutation<
      { subjectId: string },
      { meetingId: string; subscriberService: string; eventName?: string }
    >({
      query: ({ meetingId, ...body }) => ({
        url: `/meetings/${meetingId}/subscribe`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { meetingId }) => [
        { type: 'Meeting', id: `subs-${meetingId}` },
        { type: 'Meeting', id: 'LIST' },
      ],
    }),

    unsubscribeFromMeeting: builder.mutation<
      { success: boolean },
      { meetingId: string; subjectId: string }
    >({
      query: ({ meetingId, subjectId }) => ({
        url: `/meetings/${meetingId}/subscribe/${subjectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { meetingId }) => [
        { type: 'Meeting', id: `subs-${meetingId}` },
        { type: 'Meeting', id: 'LIST' },
      ],
    }),

    joinAgent: builder.mutation<
      { botId: string; status: string },
      { meetingId: string; meetingUrl: string; botName?: string }
    >({
      query: (body) => ({
        url: '/agent/join',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Meeting', id: 'LIST' }],
    }),

    leaveAgent: builder.mutation<{ success: boolean }, { meetingId: string }>({
      query: (body) => ({
        url: '/agent/leave',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { meetingId }) => [
        { type: 'Meeting', id: 'LIST' },
        { type: 'Meeting', id: meetingId },
      ],
    }),

    // ── Planning (Tasks) ──────────────────────────────────────────────
    getTasks: builder.query<
      Task[],
      { ownerId?: string; status?: string; page?: number; limit?: number } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.ownerId) searchParams.set('ownerId', params.ownerId);
        if (params?.status) searchParams.set('status', params.status);
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        const qs = searchParams.toString();
        return `/planning/v1/tasks${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: { data: Task[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Planning' as const, id: _id })),
              { type: 'Planning', id: 'LIST' },
            ]
          : [{ type: 'Planning', id: 'LIST' }],
    }),

    getTask: builder.query<Task, string>({
      query: (id) => `/planning/v1/tasks/${id}`,
      // planning wraps all responses in { data: ... } via ResponseWrapperInterceptor
      transformResponse: (response: { data: Task }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Planning', id }],
    }),

    getTaskChildren: builder.query<Task[], string>({
      query: (id) => `/planning/v1/tasks/${id}/children`,
      // planning wraps all responses in { data: ... } via ResponseWrapperInterceptor
      transformResponse: (response: { data: Task[] }) => response.data,
      providesTags: (_result, _error, parentId) => [
        { type: 'Planning', id: `children-${parentId}` },
      ],
    }),

    getTaskDescendants: builder.query<Task[], string>({
      query: (id) => `/planning/v1/tasks/${id}/descendants`,
      // planning wraps all responses in { data: ... } via ResponseWrapperInterceptor
      transformResponse: (response: { data: Task[] }) => response.data,
      providesTags: (_result, _error, parentId) => [
        { type: 'Planning', id: `descendants-${parentId}` },
      ],
    }),

    /** GET /planning/v1/boards?projectId= — pre-grouped board (PRJ-4). */
    getPlanningBoard: builder.query<Record<TaskStatus, Task[]>, { projectId?: string } | void>({
      query: (params) => ({
        url: '/planning/v1/boards',
        params: params?.projectId ? { projectId: params.projectId } : undefined,
      }),
      // The planning API wraps every response in `{ data }` — unwrap so the
      // page receives the grouped map directly (same convention as the task
      // endpoints above).
      transformResponse: (response: { data: Record<TaskStatus, Task[]> }) => response.data,
      providesTags: (result, _error, params) => [
        { type: 'Planning', id: `board-${params?.projectId ?? 'global'}` },
        { type: 'Planning', id: 'LIST' },
      ],
    }),

    createTask: builder.mutation<Task, CreateTaskDto>({
      query: (body) => ({ url: '/planning/v1/tasks', method: 'POST', body }),
      invalidatesTags: [{ type: 'Planning', id: 'LIST' }],
    }),

    updateTask: builder.mutation<Task, { id: string; body: UpdateTaskDto }>({
      query: ({ id, body }) => ({ url: `/planning/v1/tasks/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Planning', id },
        { type: 'Planning', id: 'LIST' },
      ],
    }),

    deleteTask: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/planning/v1/tasks/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Planning', id: 'LIST' }],
    }),

    // ── Repositories ───────────────────────────────────────────────────────
    getRepositories: builder.query<Repository[], { page?: number; pageSize?: number } | void>({
      query: (params) => ({
        url: '/core-proxy/repositories',
        params: params || undefined,
      }),
      providesTags: (result) => {
        // Shape-agnostic: backend may return `[]` or `{ data: [], pagination }`.
        const list = Array.isArray(result)
          ? result
          : Array.isArray((result as { data?: unknown } | undefined)?.data)
            ? (result as unknown as { data: Repository[] }).data
            : [];
        return [
          ...list.map(({ _id }) => ({ type: 'Repository' as const, id: _id })),
          { type: 'Repository', id: 'LIST' },
        ];
      },
    }),

    getRepositoryById: builder.query<Repository, string>({
      query: (id) => `/core-proxy/repositories/${id}`,
      providesTags: (result) => (result ? [{ type: 'Repository', id: result._id }] : []),
    }),

    createRepository: builder.mutation<Repository, Partial<Repository>>({
      query: (body) => ({
        url: '/core-proxy/repositories',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Repository', id: 'LIST' }],
    }),

    updateRepository: builder.mutation<Repository, { id: string; body: Partial<Repository> }>({
      query: ({ id, body }) => ({
        url: `/core-proxy/repositories/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Repository', id: result._id },
              { type: 'Repository', id: 'LIST' },
            ]
          : [{ type: 'Repository', id: 'LIST' }],
    }),

    deleteRepository: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/core-proxy/repositories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Repository', id: 'LIST' }],
    }),

    // ── Branches ───────────────────────────────────────────────────────────
    getBranches: builder.query<Branch[], string>({
      query: (repoId) => `/core-proxy/repositories/${repoId}/branches`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Branch' as const, id: _id })),
              { type: 'Branch', id: 'LIST' },
            ]
          : [{ type: 'Branch', id: 'LIST' }],
    }),

    getBranchById: builder.query<Branch, { repoId: string; branchId: string }>({
      query: ({ repoId, branchId }) => `/core-proxy/repositories/${repoId}/branches/${branchId}`,
      providesTags: (result) => (result ? [{ type: 'Branch', id: result._id }] : []),
    }),

    createBranch: builder.mutation<Branch, { repoId: string; body: Partial<Branch> }>({
      query: ({ repoId, body }) => ({
        url: `/core-proxy/repositories/${repoId}/branches`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Branch', id: 'LIST' },
        { type: 'Repository', id: 'LIST' },
      ],
    }),

    updateBranch: builder.mutation<
      Branch,
      { repoId: string; branchId: string; body: Partial<Branch> }
    >({
      query: ({ repoId, branchId, body }) => ({
        url: `/core-proxy/repositories/${repoId}/branches/${branchId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Branch', id: result._id },
              { type: 'Branch', id: 'LIST' },
            ]
          : [{ type: 'Branch', id: 'LIST' }],
    }),

    deleteBranch: builder.mutation<{ success: boolean }, { repoId: string; branchId: string }>({
      query: ({ repoId, branchId }) => ({
        url: `/core-proxy/repositories/${repoId}/branches/${branchId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Branch', id: 'LIST' },
        { type: 'Repository', id: 'LIST' },
      ],
    }),

    triggerIndex: builder.mutation<unknown, { repoId: string; branchId: string }>({
      query: ({ repoId, branchId }) => ({
        url: `/core-proxy/repositories/${repoId}/branches/${branchId}/index`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'Branch', id: 'LIST' },
        { type: 'IndexJob', id: 'LIST' },
      ],
    }),

    triggerFullReprocess: builder.mutation<unknown, { repoId: string; branchId: string }>({
      query: ({ repoId, branchId }) => ({
        url: `/core-proxy/repositories/${repoId}/branches/${branchId}/index`,
        method: 'POST',
        params: { mode: 'full' },
      }),
      invalidatesTags: [
        { type: 'Branch', id: 'LIST' },
        { type: 'IndexJob', id: 'LIST' },
      ],
    }),

    dropIndex: builder.mutation<unknown, { repoId: string; branchId: string }>({
      query: ({ repoId, branchId }) => ({
        url: `/core-proxy/repositories/${repoId}/branches/${branchId}/index`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Branch', id: 'LIST' }],
    }),

    getIndexStatus: builder.query<IndexStatus, { repoId: string; branchId: string }>({
      query: ({ repoId, branchId }) =>
        `/core-proxy/repositories/${repoId}/branches/${branchId}/index/status`,
      providesTags: [{ type: 'IndexJob', id: 'LIST' }],
    }),

    searchBranch: builder.mutation<
      SearchResult,
      { repoId: string; branchId: string; body: SearchQuery }
    >({
      query: ({ repoId, branchId, body }) => ({
        url: `/core-proxy/repositories/${repoId}/branches/${branchId}/search`,
        method: 'POST',
        body,
      }),
    }),

    // ── Instances ──────────────────────────────────────────────────────────
    getInstances: builder.query<DaytonaInstance[], { state?: string } | void>({
      query: (params) => ({
        url: '/core-proxy/instances',
        params: params || undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Instance' as const, id: _id })),
              { type: 'Instance', id: 'LIST' },
            ]
          : [{ type: 'Instance', id: 'LIST' }],
    }),

    getInstanceById: builder.query<DaytonaInstance, string>({
      query: (sandboxId) => `/core-proxy/instances/${sandboxId}`,
      providesTags: (result) => (result ? [{ type: 'Instance', id: result._id }] : []),
    }),

    startInstance: builder.mutation<DaytonaInstance, string>({
      query: (sandboxId) => ({
        url: `/core-proxy/instances/${sandboxId}/start`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Instance', id: 'LIST' }],
    }),

    stopInstance: builder.mutation<DaytonaInstance, string>({
      query: (sandboxId) => ({
        url: `/core-proxy/instances/${sandboxId}/stop`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Instance', id: 'LIST' }],
    }),

    archiveInstance: builder.mutation<DaytonaInstance, string>({
      query: (sandboxId) => ({
        url: `/core-proxy/instances/${sandboxId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Instance', id: 'LIST' }],
    }),

    deleteInstance: builder.mutation<{ success: boolean }, string>({
      query: (sandboxId) => ({
        url: `/core-proxy/instances/${sandboxId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Instance', id: 'LIST' }],
    }),

    forkInstance: builder.mutation<DaytonaInstance, { sandboxId: string; name?: string }>({
      query: ({ sandboxId, ...body }) => ({
        url: `/core-proxy/instances/${sandboxId}/fork`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Instance', id: 'LIST' }],
    }),

    executeInstanceCommand: builder.mutation<
      { output: string },
      { sandboxId: string; command: string; timeout?: number }
    >({
      query: ({ sandboxId, ...body }) => ({
        url: `/core-proxy/instances/${sandboxId}/execute`,
        method: 'POST',
        body,
      }),
    }),

    refreshInstance: builder.mutation<DaytonaInstance, string>({
      query: (sandboxId) => ({
        url: `/core-proxy/instances/${sandboxId}/refresh`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Instance', id: 'LIST' }],
    }),

    // ── Storage ──────────────────────────────────────────────────────────────

    // Providers
    getStorageProviders: builder.query<StorageProviderDescriptor[], void>({
      query: () => '/storage/providers',
      providesTags: ['StorageProvider'],
    }),

    // Sources
    getStorageSources: builder.query<StorageSource[], void>({
      query: () => '/storage/sources',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'StorageSource' as const, id: _id })),
              { type: 'StorageSource', id: 'LIST' },
            ]
          : [{ type: 'StorageSource', id: 'LIST' }],
    }),

    getStorageSource: builder.query<StorageSource, string>({
      query: (id) => `/storage/sources/${id}`,
      providesTags: (result) => (result ? [{ type: 'StorageSource', id: result._id }] : []),
    }),

    createStorageSource: builder.mutation<StorageSource, Partial<StorageSource>>({
      query: (body) => ({ url: '/storage/sources', method: 'POST', body }),
      invalidatesTags: [{ type: 'StorageSource', id: 'LIST' }],
    }),

    updateStorageSource: builder.mutation<
      StorageSource,
      { id: string; body: Partial<StorageSource> }
    >({
      query: ({ id, body }) => ({ url: `/storage/sources/${id}`, method: 'PATCH', body }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'StorageSource', id: result._id },
              { type: 'StorageSource', id: 'LIST' },
            ]
          : [{ type: 'StorageSource', id: 'LIST' }],
    }),

    deleteStorageSource: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/storage/sources/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'StorageSource', id: 'LIST' }],
    }),

    testStorageSource: builder.mutation<{ jobId: string }, string>({
      query: (id) => ({ url: `/storage/sources/${id}/test-connection`, method: 'POST' }),
    }),

    getStorageSourceTestResult: builder.query<{ status: string; message?: string }, string>({
      query: (jobId) => `/storage/jobs/${jobId}`,
    }),

    // Accounts (nested under sources)
    getStorageAccounts: builder.query<StorageAccount[], string>({
      query: (sourceId) => `/storage/sources/${sourceId}/accounts`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'StorageAccount' as const, id: _id })),
              { type: 'StorageAccount', id: 'LIST' },
            ]
          : [{ type: 'StorageAccount', id: 'LIST' }],
    }),

    createStorageAccount: builder.mutation<
      StorageAccount,
      { sourceId: string; body: Partial<StorageAccount> }
    >({
      query: ({ sourceId, body }) => ({
        url: `/storage/sources/${sourceId}/accounts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'StorageAccount', id: 'LIST' },
        { type: 'StorageSource', id: 'LIST' },
      ],
    }),

    updateStorageAccount: builder.mutation<
      StorageAccount,
      { accountId: string; body: Partial<StorageAccount> }
    >({
      query: ({ accountId, body }) => ({
        url: `/storage/accounts/${accountId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'StorageAccount', id: result._id },
              { type: 'StorageAccount', id: 'LIST' },
            ]
          : [{ type: 'StorageAccount', id: 'LIST' }],
    }),

    deleteStorageAccount: builder.mutation<{ success: boolean }, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/storage/accounts/${accountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'StorageAccount', id: 'LIST' },
        { type: 'StorageSource', id: 'LIST' },
      ],
    }),

    // Collections
    getStorageCollections: builder.query<StorageCollection[], void>({
      query: () => '/storage/collections',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'StorageCollection' as const, id: _id })),
              { type: 'StorageCollection', id: 'LIST' },
            ]
          : [{ type: 'StorageCollection', id: 'LIST' }],
    }),

    getStorageCollection: builder.query<StorageCollection, string>({
      query: (id) => `/storage/collections/${id}`,
      providesTags: (result) => (result ? [{ type: 'StorageCollection', id: result._id }] : []),
    }),

    createStorageCollection: builder.mutation<StorageCollection, Partial<StorageCollection>>({
      query: (body) => ({ url: '/storage/collections', method: 'POST', body }),
      invalidatesTags: [{ type: 'StorageCollection', id: 'LIST' }],
    }),

    updateStorageCollection: builder.mutation<
      StorageCollection,
      { id: string; body: Partial<StorageCollection> }
    >({
      query: ({ id, body }) => ({ url: `/storage/collections/${id}`, method: 'PATCH', body }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'StorageCollection', id: result._id },
              { type: 'StorageCollection', id: 'LIST' },
            ]
          : [{ type: 'StorageCollection', id: 'LIST' }],
    }),

    deleteStorageCollection: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/storage/collections/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'StorageCollection', id: 'LIST' }],
    }),

    updateStorageCollectionSettings: builder.mutation<
      StorageCollection,
      { id: string; body: Partial<StorageCollectionSettings> }
    >({
      query: ({ id, body }) => ({
        url: `/storage/collections/${id}/settings`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'StorageCollection', id: result._id },
              { type: 'StorageCollection', id: 'LIST' },
            ]
          : [{ type: 'StorageCollection', id: 'LIST' }],
    }),

    // TODO: getStorageCollectionStats endpoint doesn't exist in the service yet
    // getStorageCollectionStats: builder.query<StorageStats, string>({
    //   query: (id) => `/storage/collections/${id}/stats`,
    //   providesTags: (_result, _error, id) => [{ type: 'StorageCollection', id: `stats-${id}` }],
    // }),

    // Files
    getStorageFiles: builder.query<StorageFile[], { collectionId?: string; search?: string }>({
      query: ({ collectionId, search }) => ({
        url: `/storage/files`,
        params: { ...(collectionId ? { collectionId } : {}), ...(search ? { search } : {}) },
      }),
      // Backend returns a paginated wrapper { data, pagination }; unwrap to the flat array.
      transformResponse: (response: { data: StorageFile[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'StorageFile' as const, id: _id })),
              { type: 'StorageFile', id: 'LIST' },
            ]
          : [{ type: 'StorageFile', id: 'LIST' }],
    }),

    getStorageFile: builder.query<StorageFile, { fileId: string }>({
      query: ({ fileId }) => `/storage/files/${fileId}`,
      providesTags: (result) => (result ? [{ type: 'StorageFile', id: result._id }] : []),
    }),

    getStorageFileVersions: builder.query<StorageFileVersion[], { fileId: string }>({
      query: ({ fileId }) => `/storage/files/${fileId}/versions`,
      providesTags: (_result, _error, { fileId }) => [
        { type: 'StorageFile', id: `versions-${fileId}` },
      ],
    }),

    deleteStorageFile: builder.mutation<{ success: boolean }, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/storage/files/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'StorageFile', id: 'LIST' },
        { type: 'StorageCollection', id: 'LIST' },
      ],
    }),

    verifyStorageFile: builder.mutation<{ jobId: string }, { fileId: string }>({
      query: ({ fileId }) => ({
        url: `/storage/files/${fileId}/verify`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'StorageFile', id: 'LIST' }],
    }),

    // Uploads
    createStorageUpload: builder.mutation<
      StorageUpload,
      { collectionId: string; fileName: string; size: number; sha256: string }
    >({
      query: (body) => ({
        url: `/storage/uploads`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'StorageUpload', id: 'LIST' }],
    }),

    completeStorageUpload: builder.mutation<StorageUpload, { uploadId: string }>({
      query: ({ uploadId }) => ({
        url: `/storage/uploads/${uploadId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'StorageUpload', id: 'LIST' },
        { type: 'StorageFile', id: 'LIST' },
      ],
    }),

    abortStorageUpload: builder.mutation<{ success: boolean }, { uploadId: string }>({
      query: ({ uploadId }) => ({
        url: `/storage/uploads/${uploadId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'StorageUpload', id: 'LIST' }],
    }),

    getStorageUploadStatus: builder.query<StorageUpload, string>({
      query: (jobId) => `/storage/jobs/${jobId}`,
    }),
  }),
});

export type ApiSlice = typeof apiSlice;

// Export auto-generated hooks
export const {
  useGetDashboardStatsQuery,
  useGetRecentActivityQuery,
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useDeactivateUserMutation,
  useGetPoliciesQuery,
  useGetPolicyQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useDeletePolicyMutation,
  useReactivatePolicyMutation,
  useEvaluatePolicyMutation,
  useGetJobsQuery,
  useGetJobCountsQuery,
  useGetJobQuery,
  useCancelJobMutation,
  useGetServicesQuery,
  useGetServiceQuery,
  useGetEnvironmentConfigQuery,
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetFeatureFlagsQuery,
  useToggleFeatureFlagMutation,
  useGetMeetingsQuery,
  useGetMeetingQuery,
  useGetMeetingSubscribersQuery,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
  useStartMeetingMutation,
  useEndMeetingMutation,
  useDeleteMeetingMutation,
  useSubscribeToMeetingMutation,
  useUnsubscribeFromMeetingMutation,
  useJoinAgentMutation,
  useLeaveAgentMutation,
  useGetRepositoriesQuery,
  useGetRepositoryByIdQuery,
  useCreateRepositoryMutation,
  useUpdateRepositoryMutation,
  useDeleteRepositoryMutation,
  useGetBranchesQuery,
  useGetBranchByIdQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useTriggerIndexMutation,
  useTriggerFullReprocessMutation,
  useDropIndexMutation,
  useGetIndexStatusQuery,
  useSearchBranchMutation,
  useGetInstancesQuery,
  useGetInstanceByIdQuery,
  useStartInstanceMutation,
  useStopInstanceMutation,
  useArchiveInstanceMutation,
  useDeleteInstanceMutation,
  useForkInstanceMutation,
  useExecuteInstanceCommandMutation,
  useRefreshInstanceMutation,
  useGetTasksQuery,
  useGetTaskQuery,
  useGetTaskChildrenQuery,
  useGetTaskDescendantsQuery,
  useGetPlanningBoardQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetStorageProvidersQuery,
  useGetStorageSourcesQuery,
  useGetStorageSourceQuery,
  useCreateStorageSourceMutation,
  useUpdateStorageSourceMutation,
  useDeleteStorageSourceMutation,
  useTestStorageSourceMutation,
  useGetStorageSourceTestResultQuery,
  useGetStorageAccountsQuery,
  useCreateStorageAccountMutation,
  useUpdateStorageAccountMutation,
  useDeleteStorageAccountMutation,
  useGetStorageCollectionsQuery,
  useGetStorageCollectionQuery,
  useCreateStorageCollectionMutation,
  useUpdateStorageCollectionMutation,
  useDeleteStorageCollectionMutation,
  useUpdateStorageCollectionSettingsMutation,
  useGetStorageFilesQuery,
  useGetStorageFileQuery,
  useGetStorageFileVersionsQuery,
  useDeleteStorageFileMutation,
  useVerifyStorageFileMutation,
  useCreateStorageUploadMutation,
  useCompleteStorageUploadMutation,
  useAbortStorageUploadMutation,
  useGetStorageUploadStatusQuery,
} = apiSlice;
